<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { getIvrMenus } from 'dashboard/api/relayVoiceManager';
import {
  getInboundAgents,
  getInboundSettings,
  getOutboundPolicy,
  getRecordingSettings,
  saveInboundSettings,
  saveOutboundPolicy,
  saveRecordingSettings,
} from 'dashboard/api/relayVoiceInbound';
import { parsePrefixes } from './helpers';

// How inbound calls reach the Customer Success team, who can take them now,
// how long recordings are kept, and the outbound premium-rate blocklist.
const { t } = useI18n();
const { accountId } = useAccount();

const state = ref('loading');
const queue = ref(null);
const menus = ref([]);
const agents = ref([]);
const agentsLoading = ref(false);
const recording = ref(null);
const policy = ref(null);
const prefixText = ref('');
const saving = ref('');

const WAITS = [30, 60, 90, 120, 180, 300, 600];
const RINGS = [10, 15, 20, 30, 45, 60];
const ANNOUNCE = [0, 15, 30, 45, 60, 120];
const MESSAGE_LENGTHS = [30, 60, 120, 180, 300];

const minutesLabel = seconds =>
  seconds >= 60 && seconds % 60 === 0
    ? t('TEAM_LEAD.CALLS.MINUTES', { n: seconds / 60 })
    : t('TEAM_LEAD.CALLS.SECONDS', { n: seconds });
const available = computed(
  () => agents.value.filter(a => a.queue_status === 'Available').length
);
const prefixes = computed(() => parsePrefixes(prefixText.value));

const loadAgents = async () => {
  agentsLoading.value = true;
  try {
    agents.value = (await getInboundAgents(accountId.value)).data;
  } catch {
    agents.value = [];
  } finally {
    agentsLoading.value = false;
  }
};

const load = async () => {
  try {
    const [settings, menuList, retention, outbound] = await Promise.all([
      getInboundSettings(accountId.value),
      getIvrMenus(accountId.value).catch(() => ({ data: [] })),
      getRecordingSettings(accountId.value),
      getOutboundPolicy(accountId.value),
    ]);
    queue.value = settings;
    menus.value = (menuList.data || []).filter(
      menu => ![false, 'false'].includes(menu.enabled)
    );
    recording.value = retention;
    policy.value = outbound;
    prefixText.value = (outbound.blocked_prefixes || []).join(', ');
    state.value = 'ok';
    loadAgents();
  } catch (error) {
    state.value = error.response?.status === 503 ? 'not_configured' : 'error';
  }
};

const run = async (key, action, done) => {
  saving.value = key;
  try {
    await action();
    useAlert(done);
  } catch (error) {
    useAlert(error.response?.data?.error || t('TEAM_LEAD.GENERIC_ERROR'));
  } finally {
    saving.value = '';
  }
};

const saveQueue = () =>
  run(
    'queue',
    async () => {
      const q = queue.value;
      queue.value = await saveInboundSettings(accountId.value, {
        strategy: q.strategy,
        max_wait_seconds: q.max_wait_seconds,
        agent_ring_seconds: q.agent_ring_seconds,
        announce_seconds: q.announce_seconds,
        voicemail_seconds: q.voicemail_seconds,
        entry_type: q.entry_type,
        entry_menu_uuid: q.entry_type === 'menu' ? q.entry_menu_uuid : null,
      });
    },
    t('TEAM_LEAD.CALLS.QUEUE_SAVED')
  );

const saveRetention = () =>
  run(
    'retention',
    async () => {
      recording.value = await saveRecordingSettings(accountId.value, {
        retention_days: Number(recording.value.retention_days),
      });
    },
    t('TEAM_LEAD.CALLS.RETENTION_SAVED')
  );

const savePolicy = () =>
  run(
    'policy',
    async () => {
      policy.value = await saveOutboundPolicy(accountId.value, {
        blocklist_enabled: policy.value.blocklist_enabled,
        blocked_prefixes: prefixes.value,
      });
      prefixText.value = policy.value.blocked_prefixes.join(', ');
    },
    t('TEAM_LEAD.CALLS.BLOCKLIST_SAVED')
  );

const useSuggested = () => {
  prefixText.value = policy.value.suggested_prefixes.join(', ');
};

onMounted(load);
defineExpose({ load });
</script>

<template>
  <woot-loading-state
    v-if="state === 'loading'"
    :message="t('TEAM_LEAD.LOADING')"
  />
  <p
    v-else-if="state !== 'ok'"
    class="rounded-xl bg-n-alpha-1 px-5 py-4 text-sm text-n-slate-11"
  >
    {{
      state === 'not_configured'
        ? t('TEAM_LEAD.NOT_CONFIGURED')
        : t('TEAM_LEAD.CALLS.UNAVAILABLE')
    }}
  </p>
  <div v-else class="flex flex-col gap-5">
    <section
      data-tour="queue"
      class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-5"
    >
      <header class="flex flex-col gap-1">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('TEAM_LEAD.CALLS.QUEUE_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 max-w-2xl">
          {{
            t('TEAM_LEAD.CALLS.QUEUE_DESCRIPTION', {
              number: queue.queue_extension,
            })
          }}
        </p>
      </header>

      <fieldset class="flex flex-col gap-2">
        <legend class="text-sm font-medium text-n-slate-12 mb-2">
          {{ t('TEAM_LEAD.CALLS.STRATEGY') }}
        </legend>
        <label
          v-for="strategy in queue.strategies"
          :key="strategy"
          class="flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer"
          :class="
            queue.strategy === strategy
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak'
          "
        >
          <input
            v-model="queue.strategy"
            type="radio"
            :value="strategy"
            class="mt-1"
          />
          <span class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t(`TEAM_LEAD.CALLS.STRATEGIES.${strategy}.TITLE`) }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t(`TEAM_LEAD.CALLS.STRATEGIES.${strategy}.BODY`) }}
            </span>
          </span>
        </label>
      </fieldset>

      <div class="grid gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('TEAM_LEAD.CALLS.MAX_WAIT') }}
          <select
            v-model.number="queue.max_wait_seconds"
            class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
          >
            <option v-for="value in WAITS" :key="value" :value="value">
              {{ minutesLabel(value) }}
            </option>
          </select>
          <span class="text-n-slate-10">{{
            t('TEAM_LEAD.CALLS.MAX_WAIT_HINT')
          }}</span>
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('TEAM_LEAD.CALLS.RING') }}
          <select
            v-model.number="queue.agent_ring_seconds"
            class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
          >
            <option v-for="value in RINGS" :key="value" :value="value">
              {{ minutesLabel(value) }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('TEAM_LEAD.CALLS.ANNOUNCE') }}
          <select
            v-model.number="queue.announce_seconds"
            class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
          >
            <option v-for="value in ANNOUNCE" :key="value" :value="value">
              {{
                value
                  ? t('TEAM_LEAD.CALLS.EVERY', { time: minutesLabel(value) })
                  : t('TEAM_LEAD.CALLS.NEVER')
              }}
            </option>
          </select>
          <span class="text-n-slate-10">{{
            t('TEAM_LEAD.CALLS.ANNOUNCE_HINT')
          }}</span>
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('TEAM_LEAD.CALLS.MESSAGE_LENGTH') }}
          <select
            v-model.number="queue.voicemail_seconds"
            class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
          >
            <option
              v-for="value in MESSAGE_LENGTHS"
              :key="value"
              :value="value"
            >
              {{ minutesLabel(value) }}
            </option>
          </select>
        </label>
        <label
          class="flex flex-col gap-1 text-xs text-n-slate-11 sm:col-span-2"
        >
          {{ t('TEAM_LEAD.CALLS.ENTRY') }}
          <select
            :value="queue.entry_type === 'menu' ? queue.entry_menu_uuid : ''"
            class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12"
            @change="
              event => {
                queue.entry_type = event.target.value ? 'menu' : 'queue';
                queue.entry_menu_uuid = event.target.value || null;
              }
            "
          >
            <option value="">{{ t('TEAM_LEAD.CALLS.ENTRY_QUEUE') }}</option>
            <option
              v-for="menu in menus"
              :key="menu.ivr_menu_uuid"
              :value="menu.ivr_menu_uuid"
            >
              {{
                t('TEAM_LEAD.CALLS.ENTRY_MENU', {
                  name: menu.name,
                  number: menu.extension,
                })
              }}
            </option>
          </select>
          <span class="text-n-slate-10">{{
            t('TEAM_LEAD.CALLS.ENTRY_HINT')
          }}</span>
        </label>
      </div>
      <Button
        :label="t('TEAM_LEAD.SAVE')"
        class="self-start"
        :is-loading="saving === 'queue'"
        @click="saveQueue"
      />
    </section>

    <section
      data-tour="queue-agents"
      class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
    >
      <header class="flex items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('TEAM_LEAD.CALLS.AGENTS_TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('TEAM_LEAD.CALLS.AGENTS_DESCRIPTION', { n: available }) }}
          </p>
        </div>
        <Button
          icon="i-lucide-refresh-cw"
          variant="ghost"
          color="slate"
          size="sm"
          :is-loading="agentsLoading"
          :aria-label="t('TEAM_LEAD.CALLS.REFRESH')"
          @click="loadAgents"
        />
      </header>
      <p v-if="!agents.length" class="text-sm text-n-slate-10">
        {{ t('TEAM_LEAD.CALLS.NO_AGENTS') }}
      </p>
      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-xs text-n-slate-10">
            <th class="text-start font-medium py-1.5">
              {{ t('TEAM_LEAD.CALLS.COLUMNS.AGENT') }}
            </th>
            <th class="text-start font-medium py-1.5">
              {{ t('TEAM_LEAD.CALLS.COLUMNS.ONLINE') }}
            </th>
            <th class="text-start font-medium py-1.5">
              {{ t('TEAM_LEAD.CALLS.COLUMNS.PHONE') }}
            </th>
            <th class="text-start font-medium py-1.5">
              {{ t('TEAM_LEAD.CALLS.COLUMNS.QUEUE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="agent in agents"
            :key="agent.extension"
            class="border-t border-n-weak"
          >
            <td class="py-2 text-n-slate-12">
              {{ agent.name }}
              <span class="text-xs text-n-slate-10 tabular-nums">
                {{
                  t('TEAM_LEAD.CALLS.EXTENSION', { extension: agent.extension })
                }}
              </span>
            </td>
            <td class="py-2">
              <span
                :class="agent.available ? 'text-n-teal-11' : 'text-n-slate-10'"
              >
                {{
                  agent.available
                    ? t('TEAM_LEAD.CALLS.YES')
                    : t('TEAM_LEAD.CALLS.NO')
                }}
              </span>
            </td>
            <td class="py-2">
              <span
                :class="agent.registered ? 'text-n-teal-11' : 'text-n-slate-10'"
              >
                {{
                  agent.registered
                    ? t('TEAM_LEAD.CALLS.CONNECTED')
                    : t('TEAM_LEAD.CALLS.NOT_CONNECTED')
                }}
              </span>
            </td>
            <td class="py-2">
              <span
                class="px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  agent.queue_status === 'Available'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{
                  agent.queue_status === 'Available'
                    ? t('TEAM_LEAD.CALLS.TAKES_CALLS')
                    : t('TEAM_LEAD.CALLS.NOT_OFFERED')
                }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section
      data-tour="recording"
      class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
    >
      <header class="flex flex-col gap-1">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('TEAM_LEAD.CALLS.RECORDING_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 max-w-2xl">
          {{ t('TEAM_LEAD.CALLS.RECORDING_DESCRIPTION') }}
        </p>
      </header>
      <div class="flex flex-wrap items-end gap-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('TEAM_LEAD.CALLS.RETENTION') }}
          <input
            v-model.number="recording.retention_days"
            type="number"
            min="1"
            max="3650"
            class="!mb-0 w-32 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm text-n-slate-12 tabular-nums"
          />
        </label>
        <Button
          :label="t('TEAM_LEAD.SAVE')"
          :is-loading="saving === 'retention'"
          @click="saveRetention"
        />
        <span class="text-xs text-n-slate-10">
          {{
            t('TEAM_LEAD.CALLS.RETENTION_DEFAULT', {
              days: recording.default_retention_days,
            })
          }}
        </span>
      </div>
      <p
        class="flex items-start gap-2 rounded-lg bg-n-amber-3 px-4 py-3 text-xs text-n-amber-11"
      >
        <span class="i-ph-info-bold size-4 shrink-0" />
        {{ t('TEAM_LEAD.CALLS.PRIVACY_NOTE') }}
      </p>
    </section>

    <section
      data-tour="blocklist"
      class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
    >
      <header class="flex items-start justify-between gap-4 flex-wrap">
        <div class="flex flex-col gap-1 max-w-2xl">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('TEAM_LEAD.CALLS.OUTBOUND_TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{
              policy.destination_scope === 'international'
                ? t('TEAM_LEAD.CALLS.OUTBOUND_INTERNATIONAL')
                : t('TEAM_LEAD.CALLS.OUTBOUND_NIGERIA')
            }}
            {{
              policy.max_call_seconds
                ? t('TEAM_LEAD.CALLS.OUTBOUND_CAP', {
                    minutes: Math.round(policy.max_call_seconds / 60),
                  })
                : t('TEAM_LEAD.CALLS.OUTBOUND_NO_CAP')
            }}
          </p>
        </div>
        <label class="flex items-center gap-3 text-sm text-n-slate-12">
          <Switch
            v-model="policy.blocklist_enabled"
            data-test-id="blocklist-enabled"
          />
          {{ t('TEAM_LEAD.CALLS.BLOCKLIST') }}
        </label>
      </header>
      <p class="text-xs text-n-slate-11">
        {{ t('TEAM_LEAD.CALLS.BLOCKLIST_HINT') }}
      </p>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('TEAM_LEAD.CALLS.PREFIXES') }}
        <textarea
          v-model="prefixText"
          rows="2"
          :placeholder="t('TEAM_LEAD.CALLS.PREFIXES_PLACEHOLDER')"
          class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 tabular-nums"
        />
      </label>
      <div class="flex flex-wrap items-center gap-3">
        <Button
          :label="t('TEAM_LEAD.SAVE')"
          :is-loading="saving === 'policy'"
          :disabled="policy.blocklist_enabled && !prefixes.length"
          @click="savePolicy"
        />
        <Button
          :label="t('TEAM_LEAD.CALLS.USE_SUGGESTED')"
          variant="faded"
          color="slate"
          size="sm"
          @click="useSuggested"
        />
      </div>
    </section>
  </div>
</template>
